import { Component, computed, inject, input } from '@angular/core';
import {
  ErrorAlertComponent,
  PERMISSIONS,
  StatsCardComponent,
  StatCardData,
  TApiErrorResponse,
} from '@sandbox-platform/shared-ui';
import { DashboardStatsDTO } from '../../../types/stats.types';
import {
  CommissionInvoiceDashboardSummaryDTO,
  MoneySummaryDTO,
} from '../../../types';
import { AuthStore } from '@sandbox-platform/shared-ui';

export interface StatsState {
  dashboardStats: DashboardStatsDTO | null;
  invoiceSummary: CommissionInvoiceDashboardSummaryDTO | null;
  loading: boolean;
  invoiceSummaryLoading: boolean;
  error: string | TApiErrorResponse | null;
}

@Component({
  standalone: true,
  imports: [ErrorAlertComponent, StatsCardComponent],
  selector: 'lib-stats-section',
  template: `
    <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
      @for (card of statsCards(); track card.label) {
      <lib-stats-card [data]="card" [loading]="loadingForCard(card.label)" />
      }
    </div>
    @if (statsState().error) {
    <div class="mt-4">
      <lib-error-alert [error]="statsState().error" />
    </div>
    }
  `,
})
export class StatsSectionComponent {
  private readonly authStore = inject(AuthStore);

  statsState = input.required<StatsState>();

  protected readonly canViewTotalExpertsStat = computed(() =>
    this.authStore.hasExpertPermission(
      PERMISSIONS.EXPERT.DASHBOARD_EXPERTS_TOTAL_READ
    )
  );

  protected readonly canViewActiveExpertsStat = computed(() =>
    this.authStore.hasExpertPermission(
      PERMISSIONS.EXPERT.DASHBOARD_EXPERTS_ACTIVE_READ
    )
  );

  protected readonly canViewPresentTodayStat = computed(() =>
    this.authStore.hasExpertPermission(
      PERMISSIONS.EXPERT.DASHBOARD_ATTENDANCE_PRESENT_READ
    )
  );

  protected readonly canViewPendingInvoiceStat = computed(() =>
    this.authStore.hasAnyExpertPermission([
      PERMISSIONS.EXPERT.DASHBOARD_COMPANY_TOTALS_READ,
      PERMISSIONS.EXPERT.COMMISSION_INVOICE_READ,
      PERMISSIONS.EXPERT.COMMISSION_INVOICE_CREATE,
      PERMISSIONS.EXPERT.COMMISSION_INVOICE_CREATE_ANY,
    ])
  );

  protected readonly statsCards = computed((): StatCardData[] => {
    const dashboardStats = this.statsState().dashboardStats;

    const allCards: Array<StatCardData & { canView: boolean }> = [
      {
        label: 'Total Experts',
        value: dashboardStats?.expert_stats.total_experts ?? null,
        icon: 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
        iconColor: 'text-blue-400',
        canView: this.canViewTotalExpertsStat(),
      },
      {
        label: 'Active Experts',
        value: dashboardStats?.expert_stats.active_experts ?? null,
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        iconColor: 'text-green-400',
        canView: this.canViewActiveExpertsStat(),
      },
      {
        label: 'Organizations',
        value: dashboardStats?.total_organizations ?? null,
        icon: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m-4.5 0h4.5',
        iconColor: 'text-purple-400',
        canView: this.canViewActiveExpertsStat(),
      },
      {
        label: 'Present Today',
        value: dashboardStats?.today_attendance.present || null,
        icon: 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
        iconColor: 'text-yellow-400',
        canView: this.canViewPresentTodayStat(),
      },
      {
        label: 'Pending Invoices',
        value: this.pendingInvoiceValue(),
        supportingText: this.pendingInvoiceSupportingText(),
        icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z',
        iconColor: 'text-indigo-400',
        canView: this.canViewPendingInvoiceStat(),
      },
    ];

    return allCards.filter((card) => card.canView);
  });

  protected loadingForCard(label: string): boolean {
    return label === 'Pending Invoices'
      ? this.statsState().invoiceSummaryLoading
      : this.statsState().loading;
  }

  private pendingInvoiceValue(): string | number | null {
    const summary = this.statsState().invoiceSummary;
    if (!summary) return null;

    const totals = summary.pending_invoice_totals ?? [];
    if (totals.length === 1) {
      return this.formatMoney(totals[0]);
    }

    return summary.pending_invoice_count;
  }

  private pendingInvoiceSupportingText(): string {
    const summary = this.statsState().invoiceSummary;
    if (!summary) return '';

    const scopeLabel = summary.scope === 'company' ? 'company' : 'organization';
    const countLabel = `${summary.pending_invoice_count} invoice${
      summary.pending_invoice_count === 1 ? '' : 's'
    }`;

    if ((summary.pending_invoice_totals ?? []).length > 1) {
      return `${countLabel} across currencies, ${scopeLabel}`;
    }

    return `${countLabel}, ${scopeLabel}`;
  }

  private formatMoney(total: MoneySummaryDTO): string {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: total.currency || 'KES',
      maximumFractionDigits: 2,
    }).format((total.amount_minor ?? 0) / 100);
  }
}
